import type { Location } from '@models/location';
import type { Lookup } from '@models/lookup';
import type { Org } from '@models/org';

/** Anything a user can hold roles in: an organization (tenant or group) or a location. */
export type ScopeItem = Org | Location;

export function isLocation(item: ScopeItem): item is Location {
  return 'additionalOrgIds' in item;
}

export function isGroup(item: ScopeItem): item is Org {
  return !isLocation(item) && item.type === 'group';
}

/**
 * The tenant an item belongs to, or undefined for tenants themselves and for the global organization.
 * Roles, alerts and billing all live on this one.
 */
export function getParentOrgId(item: ScopeItem): string | undefined {
  if (isLocation(item)) {
    return item.parentOrgId || undefined;
  }

  return item.type === 'group' ? (item.parentOrgId ?? undefined) : undefined;
}

/**
 * The organizations a claim held for this item can come from: its parent tenant, plus - for a location -
 * the groups it is associated with. Mirrors `ExpandScope` in the backend's AuthorizationService.
 */
export function getScopeOrgIds(item: ScopeItem): string[] {
  const parentOrgId = getParentOrgId(item);
  const parents = parentOrgId ? [parentOrgId] : [];

  return isLocation(item) ? [...parents, ...item.additionalOrgIds] : parents;
}

/**
 * The organizations whose roles can be assigned to a user for this item: the item itself and, since groups
 * and locations borrow their tenant's roles, that tenant. Global roles are always available on top.
 */
export function getRoleOrgIds(item: ScopeItem): string[] {
  const parentOrgId = getParentOrgId(item);

  return parentOrgId ? [item.id, parentOrgId] : [item.id];
}

/** The groups belonging to the given tenant. */
export function getGroups(orgs: Iterable<Org> | undefined, parentOrgId: string): Org[] {
  return [...(orgs ?? [])].filter((org) => org.type === 'group' && org.parentOrgId === parentOrgId);
}

/** Resolves a scope id to the item it refers to, looking at locations first. */
export function findScopeItem(
  id: string,
  locations: Lookup<Location> | undefined,
  orgs: Lookup<Org> | undefined,
): ScopeItem | undefined {
  return locations?.get(id) ?? orgs?.get(id);
}
